import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import CustomSelect from '../../../ui/CustomSelect';
import apiClient from '../../../../api/apiClient';

const statusOptions = [
  { value: 'active', label: 'Aktif' },
  { value: 'inactive', label: 'Tidak Aktif' },
];

export default function DepartmentModal({ open, data, saving, onClose, onSave }) {
  const [form, setForm] = useState(data || {});
  const [errors, setErrors] = useState({
    name: '',
  });

  // Function to generate code from name (client-side helper)
  const generateCodeFromName = name => {
    if (!name || name.trim() === '') {
      return '';
    }

    // Remove extra spaces and split by space
    const words = name.trim().split(/\s+/);

    // If only one word, take first 3 uppercase letters
    if (words.length === 1) {
      return words[0].substring(0, 3).toUpperCase().padEnd(3, 'X');
    }

    // If multiple words, take first letter of each word (up to 3 words)
    const initials = words
      .slice(0, 3)
      .map(word => word.charAt(0).toUpperCase())
      .join('');

    // Ensure at least 2 characters
    return initials.length >= 2 ? initials : initials.padEnd(2, 'X').toUpperCase();
  };

  useEffect(() => {
    setForm(data || {});
    // Reset errors when modal opens/closes
    setErrors({ name: '' });
  }, [data, open]);

  // Generate code when name changes (for both new and edit)
  useEffect(() => {
    if (form && form.name) {
      const generateCode = async () => {
        try {
          const response = await apiClient.get('/hris/departments/generate-code', {
            params: { name: form.name },
          });
          if (response && response.code) {
            setForm(prev => ({
              ...prev,
              code: response.code,
            }));
          }
        } catch (error) {
          // Error generating department code from backend
          // Fallback to client-side generation
          const generatedCode = generateCodeFromName(form.name);
          if (generatedCode) {
            setForm(prev => ({
              ...prev,
              code: generatedCode,
            }));
          }
        }
      };

      // Debounce the code generation
      const timeoutId = setTimeout(() => {
        generateCode();
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [form?.name, form]);

  if (!open) return null;

  const handleSubmit = async e => {
    e.preventDefault();

    // Reset errors
    const newErrors = { name: '' };
    let hasError = false;

    // Validate required fields
    if (!form?.name || form.name.trim() === '') {
      newErrors.name = 'Nama Divisi wajib diisi';
      hasError = true;
    }

    if (hasError) {
      setErrors(newErrors);
      return;
    }

    // Clear errors if validation passes
    setErrors({ name: '' });

    // Ensure code is generated if not provided
    let codeToUse = form?.code;
    if (!codeToUse || codeToUse.trim() === '') {
      if (form?.name) {
        try {
          // Try to generate from backend first
          const response = await apiClient.get('/hris/departments/generate-code', {
            params: { name: form.name },
          });
          if (response && response.code) {
            codeToUse = response.code;
          } else {
            codeToUse = generateCodeFromName(form.name);
          }
        } catch (error) {
          // Fallback to client-side generation
          codeToUse = generateCodeFromName(form.name);
        }
      } else {
        alert('Kode divisi tidak dapat di-generate. Pastikan nama divisi sudah diisi.');
        return;
      }
    }

    // Prepare data to send
    const dataToSave = {
      ...form,
      code: codeToUse,
      status: form?.status || 'active',
      description: form?.description || '',
    };

    onSave(dataToSave);
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden animate-modal">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold">{form?.id ? 'Edit' : 'Tambah'} Divisi</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form
          id="department-form"
          onSubmit={handleSubmit}
          className="p-4 space-y-4 max-h-[60vh] overflow-y-auto"
        >
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">
                Kode <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form?.code || ''}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-gray-50"
                required
                title="Kode divisi di-generate otomatis dari nama divisi"
              />
              <p className="text-xs text-gray-500">
                Kode divisi di-generate otomatis dari nama divisi
              </p>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">
                Nama Divisi <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form?.name || ''}
                onChange={e => {
                  const newName = e.target.value;
                  setForm({ ...form, name: newName });
                  // Clear error when user starts typing
                  if (errors.name) {
                    setErrors({ ...errors, name: '' });
                  }
                  // Code will be generated in useEffect when name changes
                }}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                  errors.name
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:ring-emerald-500'
                }`}
                required
              />
              {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
              {!errors.name && form?.name && (
                <p className="text-xs text-gray-500">
                  Kode akan di-generate otomatis dari nama divisi
                </p>
              )}
            </div>
            <div className="space-y-1 md:col-span-2">
              <label className="text-sm font-medium text-gray-700">Status</label>
              <CustomSelect
                value={form?.status || 'active'}
                onChange={val => setForm({ ...form, status: val })}
                options={statusOptions}
                placeholder="Pilih status"
                width="w-full"
              />
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Deskripsi</label>
            <textarea
              value={form?.description || ''}
              onChange={e => setForm({ ...form, description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
            />
          </div>
        </form>
        <div className="flex justify-end gap-3 p-4 border-t bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100 font-medium"
          >
            Batal
          </button>
          <button
            type="submit"
            form="department-form"
            disabled={saving}
            className="px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50 font-medium"
          >
            {saving ? 'Menyimpan...' : 'Simpan'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
